import { Instance } from 'mobx-state-tree';
import * as React from 'react';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import { TLCode } from '../../common/enums';
import { DutyAssignment } from '../../store/models/DutyAssignment';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { Group } from '../../store/models/Group';
import { Secretariat } from '../../store/models';
import DataSource from 'devextreme/data/data_source';
import { DataGrid } from 'devextreme-react';
import {
  Column,
  DataGridTypes,
  Export,
  FilterPanel,
  FilterRow,
  HeaderFilter,
  Pager,
  Paging,
  SearchPanel,
  Sorting,
} from 'devextreme-react/cjs/data-grid';
import { renderGridDateWithDay } from '../../store/utils';
import { Workbook } from 'exceljs';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { saveAs } from 'file-saver';
import { Container } from '../common/styledComponents/StyledComponents';

interface DutiesSearchResultProps extends WithTranslateProps {
  duties: Array<Instance<typeof DutyAssignment>>;
  onListDutySelect: (selection: any) => void;
  groups: Array<Instance<typeof Group>>;
  secretariats: Array<Instance<typeof Secretariat>>;
}

@observer
class DutiesSearchResult extends React.Component<DutiesSearchResultProps> {
  buildGridData = () => {
    const data = this.props.duties.map((d) => ({
      id: d.dutyAssignmentId,
      dutyType: this.props.translate(d.duty!.dutyType!.type === 'DAY' ? TLCode.DutyTypeDay : TLCode.DutyTypeNight),
      dutyId: d.duty!.dutyId,
      date: d.duty!.dutyDate.date,
      pharmacyId: d.pharmacyId,
      pharmacyName: d !== undefined && d.pharmacySimple ? d.pharmacySimple!.name : '',
      level: d !== undefined && d.pharmacySimple ? d.pharmacySimple!.level : '',
      groupName:
        d !== undefined && d.pharmacySimple && d.pharmacySimple.groupId
          ? this.props.groups.find((g) => g.groupId === d.pharmacySimple!.groupId)!.name
          : '',
      secretariat:
        d !== undefined && d.pharmacySimple && d.pharmacySimple.secretariatId
          ? this.props.secretariats!.find((s) => s.secretariatId === d.pharmacySimple!.secretariatId)!.code
          : '',
      pharmacyApbNumber: d !== undefined && d.pharmacySimple ? d.pharmacySimple!.apbNumber : '',
      pharmacyAddress: d !== undefined && d.pharmacySimple ? d.pharmacySimple!.address : '',
      pharmacyCity: d !== undefined && d.pharmacySimple ? d.pharmacySimple!.city : '',
      pharmacyZip: d !== undefined && d.pharmacySimple ? d.pharmacySimple!.zipCode : '',
      pharmacyPhoneNumber: d !== undefined && d.pharmacySimple ? d.pharmacySimple!.mobile : '',
    }));

    return new DataSource({
      store: { type: 'array', key: 'id', data: data },
      langParams: {
        locale: 'fr',
        collatorOptions: {
          sensitivity: 'base',
        },
      },
    });
  };

  onExporting = (e: DataGridTypes.ExportingEvent) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Main sheet');

    exportDataGrid({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Gardes-List.xlsx');
      });
    });
  };

  render() {
    const { onListDutySelect, translate } = this.props;

    return (
      <Container>
        <DataGrid
          dataSource={this.buildGridData()}
          rowAlternationEnabled={true}
          showBorders={true}
          width="100%"
          allowColumnResizing={true}
          columnResizingMode={'widget'}
          onRowClick={(ev) => onListDutySelect(ev.data)}
          onExporting={this.onExporting}
        >
          <Export enabled={true} texts={{ exportAll: translate(TLCode.GRID_EXCEL_EXPORT) }} />
          <Sorting mode="multiple" />
          <FilterRow visible={true} applyFilter={'auto'} />
          <SearchPanel visible={true} highlightCaseSensitive={true} width={400} />
          <HeaderFilter visible={true} />
          <FilterPanel visible={true} />
          <Column
            caption={this.props.translate(TLCode.DutiesHeaderDate)}
            dataType="date"
            format="dd/MM/yyyy"
            dataField="date"
            cellRender={renderGridDateWithDay}
            width={120}
            sortOrder="asc"
            sortIndex={0}
          />
          <Column
            caption={this.props.translate(TLCode.DutiesHeaderHourRange)}
            dataField="dutyType"
            width={100}
            sortOrder="desc"
            sortIndex={2}
          />
          <Column
            caption={this.props.translate(TLCode.DutiesHeaderPharmacyApbNumber)}
            dataField="pharmacyApbNumber"
            width={70}
          />
          <Column
            caption={this.props.translate(TLCode.DutiesHeaderPharmacyName)}
            dataField="pharmacyName"
            width={200}
            sortOrder="asc"
            sortIndex={1}
          />
          <Column caption={this.props.translate(TLCode.PHARMACY_Level)} dataField="level" width={75} />
          <Column caption={this.props.translate(TLCode.PHARMACIES_Group)} dataField="groupName" width={75} />
          <Column caption={this.props.translate(TLCode.DutiesHeaderAddress)} dataField="pharmacyAddress" />
          <Column caption={this.props.translate(TLCode.DutiesHeaderZip)} dataField="pharmacyZip" width={70} />
          <Column caption={this.props.translate(TLCode.DutiesHeaderCity)} dataField="pharmacyCity" />
          <Column caption={this.props.translate(TLCode.PHARMACIES_Secretariat)} dataField="secretariat" width={100} />
          <Pager
            visible={true}
            allowedPageSizes={[5, 10, 20, 50, 100]}
            showPageSizeSelector={true}
            showInfo={true}
            showNavigationButtons={true}
          />
          <Paging defaultPageSize={20} />
        </DataGrid>
      </Container>
    );
  }
}

export default withTranslate(DutiesSearchResult);
